import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  TemplateRef,
  Renderer2,
  forwardRef,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  Inject,
  ChangeDetectionStrategy,
  Injector,
} from "@angular/core";
import { trigger, state, style } from "@angular/animations";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { DomHandler } from "@bfx/primeng/dom";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { BfxTimeInputComponent, INPUT_TIME_SHOWSECONDS } from "../bfx-time-input.component";
import { ICNInputControlState } from "../models/bfx-interface-controls.enums";
import { moment } from "shared/cubiq-model-multi-asset";

export const CALENDAR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxCalendarComponent),
  multi: true,
};

export interface LocaleSettings {
  firstDayOfWeek: number;
  dayNames: string[];
  dayNamesShort: string[];
  dayNamesMin: string[];
  monthNames: string[];
  monthNamesShort: string[];
  today: string;
  clear: string;
}
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OETimeMachineService } from "app/order-entry/services/basic/oe-time-machine.service";
import { ICAutocompleteGenericModel } from "../models/bfx-interface-controls.models";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import { DomHandlerService, DOM_HANDLER_MODE_TOKEN } from "@bfx/primeng/dom";
import { BfxAfterViewInitPromise } from "../../../../bfx-common/helpers/bfx-after-view-init-promise";
import { PrimeTemplate } from "@bfx/primeng/api";
import { ComponentMixin } from "../../../../../shared/base-components/mixins/mixin";
import {
  DisabledMixin,
  DisabledMixinInputs,
} from "../../../../../shared/base-components/mixins/disabled";
import { EmphasisMixin } from "../../../../../shared/base-components/mixins/emphasis";
import {
  FieldIconsMixin,
  FieldIconsMixinInputs,
} from "../../../../../shared/base-components/mixins/field-icons";
import {
  FieldStateMixin,
  FieldStateMixinInputs,
} from "../../../../../shared/base-components/mixins/field-state";
import {
  FieldSuffixMixin,
  FieldSuffixMixinInputs,
} from "../../../../../shared/base-components/mixins/field-suffix";
import {
  InputLabelMixin,
  InputLabelMixinInputs,
} from "../../../../../shared/base-components/mixins/input-label";
import {
  InputtedMixin,
  InputtedMixinInputs,
} from "../../../../../shared/base-components/mixins/inputted";
import {
  InputLabelStateMixin,
  InputLabelStateMixinInputs,
} from "../../../../../shared/base-components/mixins/label-state";
import {
  PlaceholderMixin,
  PlaceholderMixinInputs,
} from "../../../../../shared/base-components/mixins/placeholder";
import {
  RequiredMixin,
  RequiredMixinInputs,
} from "../../../../../shared/base-components/mixins/required";
import { SizeMixin, SizeMixinInputs } from "../../../../../shared/base-components/mixins/size";
import { PasswordMixinInputs } from "../../../../../shared/base-components/mixins/password";
import { BfxInputDirective } from "../../../../../shared/base-components/bfx-input";
import { BfxFieldSelectDirective } from "../../../../../shared/base-components/bfx-field-select";
import { getPath } from "../../../../../shared/helper-services/dom-path";

const Mixins = InputLabelMixin(
  EmphasisMixin(
    SizeMixin(
      FieldIconsMixin(
        FieldStateMixin(
          FieldSuffixMixin(
            InputtedMixin(
              InputLabelStateMixin(PlaceholderMixin(RequiredMixin(DisabledMixin(ComponentMixin)))),
            ),
          ),
        ),
      ),
    ),
  ),
);

@Component({
  selector: "bfx-calendar",
  templateUrl: "./html/bfx-calendar.html",
  animations: [
    trigger("overlayState", [
      state(
        "hidden",
        style({
          opacity: 0,
        }),
      ),
      state(
        "visible",
        style({
          opacity: 1,
        }),
      ),
      // transition('visible => hidden', animate('400ms ease-in')),
      // transition('hidden => visible', animate('400ms ease-out'))
    ]),
  ],
  host: {
    "[class.ui-inputwrapper-filled]": "filled",
    "[class.ui-inputwrapper-focus]": "focus",
    "[class.interface-control__bfx-calendar]": "true",
    "[class.bfx-calendar-w-btn]": "showIcon",
    "[class.interface-control__bfx-calendar-state--disabled]":
      "disabled || data.locked || data.disabled",
    "[class.interface-control__bfx-calendar-state--invalid]": "state === 1",
    "[class.interface-control__bfx-calendar-state--active]": "state === 2 && !data.locked",
    "[class.interface-control__bfx-calendar-state--inactive]": "state === 3 && !data.locked",
    "[style.display]": "showIcon ? 'flex' : 'none'",
  },
  inputs: [
    ...DisabledMixinInputs,
    ...FieldIconsMixinInputs,
    ...FieldStateMixinInputs,
    ...FieldSuffixMixinInputs,
    ...InputLabelMixinInputs,
    ...InputLabelStateMixinInputs,
    ...InputtedMixinInputs,
    ...PasswordMixinInputs,
    ...PlaceholderMixinInputs,
    ...RequiredMixinInputs,
    ...SizeMixinInputs,
  ],
  outputs: ["fieldIconClick"],
  providers: [CALENDAR_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxCalendarComponent
  extends Mixins
  implements
    AfterViewInit,
    AfterViewChecked,
    BfxAfterViewInitPromise,
    OnInit,
    OnDestroy,
    ControlValueAccessor
{
  LOG_PREFIX: string = "[BfxCalendarComponent] ";
  TRACE_LOGGING: boolean = false;
  isButtonClick: boolean = false;

  private traceLog(logString: string) {
    if (this.TRACE_LOGGING) logger.out.info(this.LOG_PREFIX + logString);
  }

  @Input() target: string;
  @Input() defaultDate: Date;

  @Input() style: string;

  @Input() styleClass: string;

  @Input() inputStyle: string;

  @Input() inputId: string;

  @Input() name: string;

  @Input() inputStyleClass: string;

  _disabled: boolean;

  get disabled() {
    return this._disabled;
  }

  @Input()
  set disabled(value) {
    this._disabled = value;

    this.state = this._state;
  }

  @Input() dateFormat: string = "mm/dd/yy";

  @Input()
  altDateFormats: Array<string> = ["d-m-y", "d/m/y", "dd/mm/yy", "dd-M-y", "dd/M/y"];

  @Input() showDateInput: boolean = true;

  @Input() showOtherMonths: boolean = true;

  @Input() showActionsBar: boolean = true;

  @Input() alignToAutocompleteInput: boolean = true;

  @Input() selectOtherMonths: boolean = true;

  @Input() showIcon: boolean;

  @Input() icon: string = "fa icon-calendar_month";

  @Input() appendTo: any;

  @Input() readonlyInput: boolean;

  @Input() shortYearCutoff: any = "+10";

  @Input() monthNavigator: boolean;

  @Input() yearNavigator: boolean;

  @Input() yearRange: string;

  @Input() hourFormat: string = "24";

  @Input() timeOnly: boolean;

  @Input() stepHour: number = 1;

  @Input() stepMinute: number = 1;

  @Input() stepSecond: number = 1;

  @Input() required: boolean;

  @Input() showOnFocus: boolean = true;

  @Input() dataType: string = "date";

  @Input() label: string;

  @Input() utc: boolean;

  @Input() adjustForTimeZone: boolean = false;

  @Input() selectionMode: string = "single";

  @Input() maxDateCount: number;

  @Input() showButtonBar: boolean;

  @Input() todayButtonStyleClass: string = "ui-button-secondary";

  @Input() clearButtonStyleClass: string = "ui-button-secondary";

  @Input() autoZIndex: boolean = true;

  @Input() baseZIndex: number = 0;

  @Input() panelStyleClass: string;

  @Input() keepInvalid: boolean = false;

  @Input() hideOnDateTimeSelect: boolean = false;

  @Input() selectIsSave: boolean = false;

  @Input() autoCompletePosition: any;

  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  @Output() onBlur: EventEmitter<any> = new EventEmitter();

  @Output() onClose: EventEmitter<any> = new EventEmitter();

  @Output() onSelect: EventEmitter<any> = new EventEmitter();

  @Output() onInput: EventEmitter<any> = new EventEmitter();

  @Output() onTodayClick: EventEmitter<any> = new EventEmitter();

  @Output() onClearClick: EventEmitter<any> = new EventEmitter();

  @Output() onMonthChange: EventEmitter<any> = new EventEmitter();

  @Output() onYearChange: EventEmitter<any> = new EventEmitter();

  @Output() dateValueInput: EventEmitter<any> = new EventEmitter<any>();

  @Output() calendarValidationState: EventEmitter<any> = new EventEmitter<any>();

  @Output() onShowOverlay: EventEmitter<any> = new EventEmitter<any>();

  @Output() onHideOverlay: EventEmitter<any> = new EventEmitter<any>();

  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  @Input() tabindex: number;

  @Input() placeholder: string;

  @Input() showSeconds: boolean = false;

  @Input() updateOnSelect: boolean = false;

  @Input()
  parseInput: boolean = false;

  @ViewChild("datepicker") overlayViewChild: ElementRef;

  @ViewChild("inputfield") inputfieldViewChild: ElementRef;

  @ViewChild("inputTimeField") inputTimeField: BfxTimeInputComponent;

  _locale: LocaleSettings = {
    firstDayOfWeek: 0,
    dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    monthNamesShort: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    today: "Today",
    clear: "Clear",
  };

  data = {
    dateValue: null,
    timeValue: null,
    timeInputModel: null, // Time Input model
    timezoneValue: null,
    marketValue: null,
  };

  public _state: ICNInputControlState = ICNInputControlState.INACTIVE;

  prevData: any = {
    dateValue: null,
    timeValue: null,
    timezoneValue: null,
  };

  value: Date | Date[];

  public highlightValue: any;

  dates: any[];

  parseTimeInput: boolean;

  weekDays: string[];

  currentMonthText: string;

  currentMonth: number;

  currentYear: number;

  currentHour: number;

  currentMinute: number;

  currentSecond: number;

  pm: boolean;

  overlay: HTMLDivElement;

  overlayVisible: boolean;

  overlayShown: boolean;

  datepickerClick: boolean;

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  calendarElement: any;

  documentClickListener: any;

  ticksTo1970: number;

  yearOptions: number[];

  focus: boolean;

  filled: boolean;

  inputFieldValue: string = null;

  _minDateAdjustment: number = -1;

  _minDate: Date;

  _maxDate: Date;

  _showTime: boolean;

  preventDocumentListener: boolean;

  dateTemplate: TemplateRef<any>;

  _disabledDates: Array<Date>;

  _disabledDays: Array<number>;

  afterViewInitResolve?: Function;
  afterViewInitReady: Promise<void> = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  @Input() get minDateAdjustment(): number {
    return this._minDateAdjustment;
  }
  set minDateAdjustment(adjustment: number) {
    this._minDateAdjustment = adjustment;
  }

  @Input() get minDate(): Date {
    return this._minDate;
  }

  set minDate(date: Date) {
    this._minDate = date;

    if (this.currentMonth != undefined && this.currentMonth != null && this.currentYear) {
      this.createMonth(this.currentMonth, this.currentYear);
    }
  }

  @Input() get maxDate(): Date {
    return this._maxDate;
  }

  set maxDate(date: Date) {
    this._maxDate = date;

    if (this.currentMonth != undefined && this.currentMonth != null && this.currentYear) {
      this.createMonth(this.currentMonth, this.currentYear);
    }
  }

  @Input() get disabledDates(): Date[] {
    return this._disabledDates;
  }

  set disabledDates(disabledDates: Date[]) {
    this._disabledDates = disabledDates;
    if (this.currentMonth != undefined && this.currentMonth != null && this.currentYear) {
      this.createMonth(this.currentMonth, this.currentYear);
    }
  }

  @Input() get disabledDays(): number[] {
    return this._disabledDays;
  }

  set disabledDays(disabledDays: number[]) {
    this._disabledDays = disabledDays;

    if (this.currentMonth != undefined && this.currentMonth != null && this.currentYear) {
      this.createMonth(this.currentMonth, this.currentYear);
    }
  }

  @Input() get showTime(): boolean {
    return this._showTime;
  }

  set showTime(showTime: boolean) {
    this._showTime = showTime;

    if (this.currentHour === undefined && !Array.isArray(this.value)) {
      this.initTime(this.value || new Date());
    }
    this.updateInputField();
  }

  get locale() {
    return this._locale;
  }

  @Input()
  set locale(newLocale: LocaleSettings) {
    this._locale = newLocale;
    this.createWeekDays();
    this.createMonth(this.currentMonth, this.currentYear);
  }

  @Input() get state(): ICNInputControlState {
    return this._state;
  }

  set state(value: ICNInputControlState) {
    this._state = value;
    switch (value) {
      case ICNInputControlState.ACTIVE: {
        this.inputLabelState = "enabled";
        this.fieldState = "active";
        break;
      }
      case ICNInputControlState.VALID: {
        this.inputLabelState = this.disabled ? "disabled" : "valid";
        this.fieldState = this.disabled ? "disabled" : "enabled";
        break;
      }
      case ICNInputControlState.INVALID: {
        this.inputLabelState = this.disabled ? "disabled" : "invalid";
        this.fieldState = this.disabled ? "disabled" : "invalid";
        break;
      }
      case ICNInputControlState.NONE:
      case ICNInputControlState.INACTIVE:
      default: {
        this.inputLabelState = this.disabled ? "disabled" : "read-only";
        this.fieldState = this.disabled ? "disabled" : "enabled";
        break;
      }
    }
    this.cd.markForCheck();
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxFieldSelect(): Record<string, any> {
    return this.fieldDirective.directiveHost(this);
  }

  @Input()
  startTemplateRef: TemplateRef<any>;

  fieldPointerOver: boolean = false;

  constructor(
    injector: Injector,
    public inputDirective: BfxInputDirective,
    public fieldDirective: BfxFieldSelectDirective,
    public el: ElementRef,
    public domHandler: DomHandler,
    public renderer: Renderer2,
    public cd: ChangeDetectorRef,
    private _oeTimeMachine: OETimeMachineService,
    @Inject(INPUT_TIME_SHOWSECONDS) public in_showSeconds: boolean,
    @Inject(DOM_HANDLER_MODE_TOKEN) public domHandlerService: DomHandlerService,
  ) {
    super(injector);
    this.size = "sm";
    this.showSeconds = in_showSeconds;
    this.fieldEndIcon = "bfx-ignore-target fa fa-fw icon-calendar_month";
    this.cd.markForCheck();
  }

  ngOnInit() {
    let date = this.defaultDate || new Date();
    this.createWeekDays();

    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.initTime(date);

    this.createMonth(this.currentMonth, this.currentYear);

    this.ticksTo1970 =
      ((1970 - 1) * 365 + Math.floor(1970 / 4) - Math.floor(1970 / 100) + Math.floor(1970 / 400)) *
      24 *
      60 *
      60 *
      10000000;

    if (this.yearNavigator && this.yearRange) {
      this.yearOptions = [];
      let years = this.yearRange.split(":"),
        yearStart = parseInt(years[0]),
        yearEnd = parseInt(years[1]);

      for (let i = yearStart; i <= yearEnd; i++) {
        this.yearOptions.push(i);
      }
    }
    if (this.panelStyleClass != null && this.panelStyleClass.length > 0) {
      this.panelStyleClass = this.panelStyleClass;
    }
  }

  ngAfterViewInit() {
    if (this.appendTo) {
      if (this.appendTo === "body") document.body.appendChild(this.overlayViewChild.nativeElement);
      else DomHandler.appendChild(this.overlayViewChild.nativeElement, this.appendTo);
    }

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.ngCalendarDecorator();
    }

    this.afterViewInitResolve();
  }

  ngCalendarDecorator(): void {
    // desktop calendar decorator code goes here;
  }

  ngAfterViewChecked() {
    if (this.overlayShown) {
      this.alignOverlay();
      this.overlayShown = false;
    }
  }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case "date":
          this.dateTemplate = item.template;
          break;

        default:
          this.dateTemplate = item.template;
          break;
      }
    });
  }

  createWeekDays() {
    this.weekDays = [];
    let dayIndex = this.locale.firstDayOfWeek;
    for (let i = 0; i < 7; i++) {
      this.weekDays.push(this.locale.dayNamesShort[dayIndex]);
      dayIndex = dayIndex == 6 ? 0 : ++dayIndex;
    }
  }

  createMonth(month: number, year: number) {
    this.dates = [];
    this.currentMonth = month;
    this.currentYear = year;
    this.currentMonthText = this.locale.monthNames[month];
    let firstDay = this.getFirstDayOfMonthIndex(month, year);
    let daysLength = this.getDaysCountInMonth(month, year);
    let prevMonthDaysLength = this.getDaysCountInPrevMonth(month, year);
    let sundayIndex = this.getSundayIndex();
    let dayNo = 1;
    let today = new Date();

    for (let i = 0; i < 6; i++) {
      let week = [];

      if (i == 0) {
        for (let j = prevMonthDaysLength - firstDay + 1; j <= prevMonthDaysLength; j++) {
          let prev = this.getPreviousMonthAndYear(month, year);
          week.push({
            day: j,
            month: prev.month,
            year: prev.year,
            otherMonth: true,
            today: this.isToday(today, j, prev.month, prev.year),
            selectable: this.isSelectable(j, prev.month, prev.year),
          });
        }

        let remainingDaysLength = 7 - week.length;
        for (let j = 0; j < remainingDaysLength; j++) {
          week.push({
            day: dayNo,
            month: month,
            year: year,
            today: this.isToday(today, dayNo, month, year),
            selectable: this.isSelectable(dayNo, month, year),
          });
          dayNo++;
        }
      } else {
        if (dayNo <= daysLength) {
          for (let j = 0; j < 7; j++) {
            if (dayNo > daysLength) {
              let next = this.getNextMonthAndYear(month, year);
              week.push({
                day: dayNo - daysLength,
                month: next.month,
                year: next.year,
                otherMonth: true,
                today: this.isToday(today, dayNo - daysLength, next.month, next.year),
                selectable: this.isSelectable(dayNo - daysLength, next.month, next.year),
              });
            } else {
              week.push({
                day: dayNo,
                month: month,
                year: year,
                today: this.isToday(today, dayNo, month, year),
                selectable: this.isSelectable(dayNo, month, year),
              });
            }

            dayNo++;
          }
        }
      }

      this.dates.push(week);
    }

    return null;
  }

  initTime(date: Date) {
    this.pm = !this.utc ? date.getHours() > 11 : date.getUTCHours() > 11;
    if (this.showTime) {
      let timeString = this._oeTimeMachine.getTimeForNowMomentWithTimezone(this.data.timezoneValue);
      if (this.adjustForTimeZone && timeString != null) {
        this.currentHour = Number(timeString.substr(0, 2));
        this.currentMinute = Number(timeString.substr(3, 2));
        this.currentSecond = date.getUTCSeconds();
      } else if (this.utc) {
        this.currentMinute = date.getUTCMinutes();
        this.currentSecond = date.getUTCSeconds();

        if (this.hourFormat == "12")
          this.currentHour = date.getUTCHours() == 0 ? 12 : date.getUTCHours() % 12;
        else this.currentHour = date.getUTCHours();
      } else {
        this.currentMinute = date.getMinutes();
        this.currentSecond = date.getSeconds();

        if (this.hourFormat == "12")
          this.currentHour = date.getHours() == 0 ? 12 : date.getHours() % 12;
        else this.currentHour = date.getHours();
      }
    } else if (this.timeOnly) {
      this.currentMinute = 0;
      this.currentHour = 0;
      this.currentSecond = 0;
    }
  }

  prevMonth(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;

      if (this.yearNavigator && this.currentYear < this.yearOptions[0]) {
        this.currentYear = this.yearOptions[this.yearOptions.length - 1];
      }
    } else {
      this.currentMonth--;
    }

    this.onMonthChange.emit({
      month: this.currentMonth + 1,
      year: this.currentYear,
    });
    this.createMonth(this.currentMonth, this.currentYear);
    event.preventDefault();
  }

  nextMonth(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;

      if (this.yearNavigator && this.currentYear > this.yearOptions[this.yearOptions.length - 1]) {
        this.currentYear = this.yearOptions[0];
      }
    } else {
      this.currentMonth++;
    }

    this.onMonthChange.emit({
      month: this.currentMonth + 1,
      year: this.currentYear,
    });
    this.createMonth(this.currentMonth, this.currentYear);
    event.preventDefault();
  }

  onDateSelect(event, dateMeta) {
    if (this.disabled || !dateMeta.selectable) {
      if (event != null) {
        event.preventDefault();
      }
      return;
    }

    this.highlightValue = null;

    if (this.isMultipleSelection() && this.isSelected(dateMeta) && Array.isArray(this.value)) {
      this.value = this.value.filter((date, i) => {
        return !this.isDateEquals(date, dateMeta);
      });
      this.updateModel(this.value);
    } else if (this.shouldSelectDate(dateMeta)) {
      if (dateMeta.otherMonth) {
        if (this.selectOtherMonths) {
          this.currentMonth = dateMeta.month;
          this.currentYear = dateMeta.year;
          this.createMonth(this.currentMonth, this.currentYear);
          this.selectDate(dateMeta);
        }
      } else {
        this.selectDate(dateMeta);
      }
      if (event != null && this.hideOnDateTimeSelect) {
        this.hideOverlay();
      }
    }
    event?.preventDefault();
  }

  shouldSelectDate(dateMeta) {
    if (this.isMultipleSelection())
      return (
        !this.maxDateCount ||
        !this.value ||
        (Array.isArray(this.value) && this.maxDateCount > this.value.length)
      );
    else return true;
  }

  updateInputField() {
    let formattedValue = "";
    let timeInputValue = "";

    if (this.value) {
      if (this.isSingleSelection()) {
        formattedValue = this.formatDateTime(this.value);
        if (this.data.timeValue != null && this.data.timezoneValue != null) {
          timeInputValue = [this.data.timeValue, this.data.timezoneValue].join(" ");
        } else {
          timeInputValue = this.data.timeValue;
        }
      } else if (this.isMultipleSelection() && Array.isArray(this.value)) {
        for (let i = 0; i < this.value.length; i++) {
          let dateAsString = this.formatDateTime(this.value[i]);
          formattedValue += dateAsString;
          if (i !== this.value.length - 1) {
            formattedValue += ", ";
          }
        }
      } else if (this.isRangeSelection() && Array.isArray(this.value)) {
        if (this.value && this.value.length >= 2) {
          let startDate = this.value[0];
          let endDate = this.value[1];

          formattedValue = this.formatDateTime(startDate);
          if (endDate) {
            formattedValue += " - " + this.formatDateTime(endDate);
          }
        }
      }
    }

    this.inputFieldValue = formattedValue; // [formattedValue, timeInputValue].join(" ");

    this.updateFilledState();
    if (this.inputfieldViewChild && this.inputfieldViewChild.nativeElement) {
      this.inputfieldViewChild.nativeElement.value = this.inputFieldValue;
    }
  }

  formatDateTime(date) {
    let formattedValue = null;
    if (date && isNaN(date) == false) {
      if (this.timeOnly) {
        formattedValue = this.formatTime(date);
      } else {
        formattedValue = CubiqTS.moment(date).format(this.dateFormat);
        if (this.showTime) {
          formattedValue += " " + this.formatTime(date);
        }
      }
    } else {
      formattedValue = "DD-M-YY";
    }

    return formattedValue;
  }

  selectDate(dateMeta) {
    let date;
    if (this.utc) date = new Date(Date.UTC(dateMeta.year, dateMeta.month, dateMeta.day));
    else date = new Date(dateMeta.year, dateMeta.month, dateMeta.day);

    if (this.showTime) {
      if (this.utc) {
        if (this.hourFormat === "12" && this.pm && this.currentHour != 12)
          date.setUTCHours(this.currentHour + 12);
        else date.setUTCHours(this.currentHour);

        date.setUTCMinutes(this.currentMinute);
        date.setUTCSeconds(this.currentSecond);
      } else {
        if (this.hourFormat === "12" && this.pm && this.currentHour != 12)
          date.setHours(this.currentHour + 12);
        else date.setHours(this.currentHour);

        date.setMinutes(this.currentMinute);
        date.setSeconds(this.currentSecond);
      }
    }

    if (this.minDate && this.minDate > date) {
      date = this.minDate;
    }

    if (this.maxDate && this.maxDate < date) {
      date = this.maxDate;
    }

    if (this.isSingleSelection()) {
      this.updateModel(date);
    } else if (this.isMultipleSelection()) {
      this.updateModel(this.value && Array.isArray(this.value) ? [...this.value, date] : [date]);
    } else if (this.isRangeSelection()) {
      if (this.value && Array.isArray(this.value)) {
        let startDate = this.value[0];
        let endDate = this.value[1];

        if (!endDate && date.getTime() >= startDate.getTime()) {
          endDate = date;
        } else {
          startDate = date;
          endDate = null;
        }

        this.updateModel([startDate, endDate]);
      } else {
        this.updateModel([date, null]);
      }
    }

    this.onSelect.emit(date);

    if (this.selectIsSave) {
      this.onSaveClick();
    }
  }

  updateModel(value) {
    this.cd.markForCheck();
    this.value = value;
    if (this.value) {
      this.data.dateValue = this.formatDateTime(this.value);
      this.state = ICNInputControlState.VALID;
      this.inputted = true;
    } else {
      this.data.dateValue = null;
      this.inputted = false;
    }
    if (this.dataType == "date") {
      this.onModelChange(this.value);
    } else if (this.dataType == "string") {
      if (this.isSingleSelection()) {
        this.onModelChange(this.formatDateTime(this.value));
      } else if (Array.isArray(this.value)) {
        let stringArrValue = null;
        if (this.value) {
          stringArrValue = this.value.map((date) => this.formatDateTime(date));
        }
        this.onModelChange(stringArrValue);
      }
    }
  }

  getFirstDayOfMonthIndex(month: number, year: number) {
    let day = new Date();
    day.setDate(1);
    day.setMonth(month);
    day.setFullYear(year);

    let dayIndex = day.getDay() + this.getSundayIndex();
    return dayIndex >= 7 ? dayIndex - 7 : dayIndex;
  }

  getDaysCountInMonth(month: number, year: number) {
    return 32 - this.daylightSavingAdjust(new Date(year, month, 32)).getDate();
  }

  getDaysCountInPrevMonth(month: number, year: number) {
    let prev = this.getPreviousMonthAndYear(month, year);
    return this.getDaysCountInMonth(prev.month, prev.year);
  }

  getPreviousMonthAndYear(month: number, year: number) {
    let m, y;

    if (month === 0) {
      m = 11;
      y = year - 1;
    } else {
      m = month - 1;
      y = year;
    }

    return { month: m, year: y };
  }

  getNextMonthAndYear(month: number, year: number) {
    let m, y;

    if (month === 11) {
      m = 0;
      y = year + 1;
    } else {
      m = month + 1;
      y = year;
    }

    return { month: m, year: y };
  }

  getSundayIndex() {
    return this.locale.firstDayOfWeek > 0 ? 7 - this.locale.firstDayOfWeek : 0;
  }

  isSelected(dateMeta): boolean {
    if (this.value) {
      if (this.isSingleSelection()) {
        return this.isDateEquals(this.value, dateMeta);
      } else if (this.isMultipleSelection() && Array.isArray(this.value)) {
        let selected = false;
        for (let date of this.value) {
          selected = this.isDateEquals(date, dateMeta);
          if (selected) {
            break;
          }
        }

        return selected;
      } else if (this.isRangeSelection()) {
        if (this.value[1])
          return (
            this.isDateEquals(this.value[0], dateMeta) ||
            this.isDateEquals(this.value[1], dateMeta) ||
            this.isDateBetween(this.value[0], this.value[1], dateMeta)
          );
        else return this.isDateEquals(this.value[0], dateMeta);
      }
    } else return false;
  }

  isDateEquals(value, dateMeta) {
    if (value)
      return (
        value.getDate() === dateMeta.day &&
        value.getMonth() === dateMeta.month &&
        value.getFullYear() === dateMeta.year
      );
    else return false;
  }

  isDateBetween(start, end, dateMeta) {
    let between: boolean = false;
    if (start && end) {
      let date: Date = new Date(dateMeta.year, dateMeta.month, dateMeta.day);
      return start.getTime() <= date.getTime() && end.getTime() >= date.getTime();
    }

    return between;
  }

  isSingleSelection(): boolean {
    return this.selectionMode === "single";
  }

  isRangeSelection(): boolean {
    return this.selectionMode === "range";
  }

  isMultipleSelection(): boolean {
    return this.selectionMode === "multiple";
  }

  isToday(today, day, month, year): boolean {
    return today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
  }

  isSelectable(day, month, year): boolean {
    let validMin = true;
    let validMax = true;
    let validDate = true;
    let validDay = true;

    if (this.minDate) {
      if (this.minDate.getFullYear() > year) {
        validMin = false;
      } else if (this.minDate.getFullYear() === year) {
        if (this.minDate.getMonth() > month) {
          validMin = false;
        } else if (this.minDate.getMonth() === month) {
          if (this.minDate.getDate() > day) {
            validMin = false;
          }
        }
      }
    }

    if (this.maxDate) {
      if (this.maxDate.getFullYear() < year) {
        validMax = false;
      } else if (this.maxDate.getFullYear() === year) {
        if (this.maxDate.getMonth() < month) {
          validMax = false;
        } else if (this.maxDate.getMonth() === month) {
          if (this.maxDate.getDate() < day) {
            validMax = false;
          }
        }
      }
    }

    if (this.disabledDates) {
      validDate = !this.isDateDisabled(day, month, year);
    }

    if (this.disabledDays) {
      validDay = !this.isDayDisabled(day, month, year);
    }

    return validMin && validMax && validDate && validDay;
  }

  isDateDisabled(day: number, month: number, year: number): boolean {
    if (this.disabledDates) {
      for (let disabledDate of this.disabledDates) {
        if (
          disabledDate.getFullYear() === year &&
          disabledDate.getMonth() === month &&
          disabledDate.getDate() === day
        ) {
          return true;
        }
      }
    }

    return false;
  }

  isDayDisabled(day: number, month: number, year: number): boolean {
    if (this.disabledDays) {
      let weekday = new Date(year, month, day);
      let weekdayNumber = weekday.getDay();
      return this.disabledDays.indexOf(weekdayNumber) !== -1;
    }
    return false;
  }

  onInputFocus(event: Event) {
    // in openfin we sometimes get this event (no difference) on both gaining and losing focus
    // so we need to not process it if we already have focus
    // @see comments on https://www.bcdfx.com/ijira/browse/OMS-30363
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.traceLog("onInputFocus");
      if (!this.focus) {
        this.focus = true;
        if (this.showOnFocus && this.isButtonClick == false) {
          this.onButtonClick(event);
        } else {
          this.isButtonClick = false;
        }
        this.state = ICNInputControlState.ACTIVE;
        this.onFocus.emit(event);
      } else {
        this.traceLog("Already have focus");
      }
    }
  }

  onInputClick(event: Event) {
    this.datepickerClick = true;
    if (this.autoZIndex) {
      this.overlayViewChild.nativeElement.style.zIndex = String(
        this.baseZIndex + ++DomHandler.zindex,
      );
    }
  }

  onInputBlur(event: FocusEvent) {
    this.traceLog("onInputBlur " + this.overlayVisible);
    this.focus = false;

    if (
      event?.relatedTarget != null &&
      event?.relatedTarget instanceof HTMLElement &&
      event.relatedTarget.classList.contains("interface-control__bfx-calendar__date")
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    } else if (
      event?.relatedTarget != null &&
      event?.relatedTarget instanceof HTMLElement &&
      event?.relatedTarget?.parentElement?.parentElement?.parentElement?.parentElement?.classList?.contains(
        "interface-control__good-from-until__input",
      )
    ) {
      const autocompleteInput = event?.relatedTarget as HTMLElement;

      setTimeout(() => {
        autocompleteInput.focus();
      }, 4);
    }

    this.onUserInput(event);
    this.updateInputField();
    this.onBlur.emit(event);
    this.state = ICNInputControlState.INACTIVE;
    if (!this.keepInvalid) {
      this.updateInputField();
    }
    this.onModelTouched();
  }

  onButtonClick(event) {
    this.initMinDate();

    this.resetCalendarToShowDate(this.data.dateValue);
    this.prevData.dateValue = this.data.dateValue;
    this.prevData.timeValue = this.data.timeValue;
    this.prevData.timezoneValue = this.data.timezoneValue;
    this.prevData.marketValue = this.data.marketValue;
    if (!this.overlayVisible) {
      this.isButtonClick = true;
      this.showOverlay();
      if (
        this.data.dateValue == null &&
        (!this.autoCompletePosition?.value ||
          this.autoCompletePosition.value === "NOW" ||
          this.autoCompletePosition.value === "GTC" ||
          this.autoCompletePosition.value === "CUSTOM")
      ) {
        if (this.adjustForTimeZone != null && this.inputTimeField.data.userTimezoneCode != null) {
          this.inputTimeField.data.timezoneValue = new ICAutocompleteGenericModel(
            this.inputTimeField.data.userTimezoneCode,
            this.inputTimeField.data.userTimezoneCode,
          );
          this.inputTimeField.data.timeValue = this._oeTimeMachine.getTimeForNowMomentWithTimezone(
            this.inputTimeField.data.userTimezoneCode,
            this.showSeconds ? "HH:mm:ss" : "HH:mm",
          );
          let nowMoment: CubiqTS.Moment = this._oeTimeMachine.createNowMoment4Timezone(
            this.inputTimeField.data.userTimezoneCode,
          );
          let now: Date = new Date(
            nowMoment.year(),
            nowMoment.month(),
            nowMoment.date(),
            nowMoment.hour(),
            nowMoment.minute(),
            nowMoment.second(),
          );
          this.data.dateValue = nowMoment.format("DD-MMM-YYYY"); ///nowMoment.date()+'-'+nowMoment.year()+'-'+nowMoment.year();
          this.minDate = now;
        } else {
          this.inputTimeField.data.timeValue = moment().format(
            this.showSeconds ? "HH:mm:ss" : "HH:mm",
          );
          if (this.inputTimeField.data.userTimezoneCode)
            this.inputTimeField.data.timezoneValue = new ICAutocompleteGenericModel(
              this.inputTimeField.data.userTimezoneCode,
              this.inputTimeField.data.userTimezoneCode,
            );
        }
        this.onTodayButtonClick(event);
      }
    } else {
      this.isButtonClick = true;
      this.hideOverlay();
    }

    if (this.inputfieldViewChild?.nativeElement != null) {
      this.inputfieldViewChild.nativeElement.select();
      this.inputfieldViewChild.nativeElement.focus();
    }

    this.datepickerClick = true;
  }

  hideOverlay() {
    this.overlayVisible = false;

    // Needed for fixing date in benchmarks
    if (this.updateOnSelect === true) {
      this.updateInputField();
    }

    this.onHideOverlay.emit();

    if (this.highlightValue != null) {
      let dateMeta = this.highlightValue;
      this.highlightValue = null;
      this.onDateSelect(null, dateMeta);
    }
  }

  private initMinDate() {
    let noMinDate: boolean = true;
    if (this._minDate) {
      noMinDate = false;
    }

    if (noMinDate) {
      let yest: Date = new Date();
      yest.setDate(yest.getDate() + this._minDateAdjustment);
      this.minDate = yest;
    }
    if (
      this.showActionsBar &&
      this.adjustForTimeZone != null &&
      this.inputTimeField.data.userTimezoneCode != null
    ) {
      let nowMoment: CubiqTS.Moment =
        this._minDateAdjustment == -1
          ? this._oeTimeMachine.createNowMoment4Timezone(this.inputTimeField.data.userTimezoneCode)
          : CubiqTS.moment();
      if (noMinDate) {
        this.minDate = new Date(
          nowMoment.year(),
          nowMoment.month(),
          nowMoment.date(),
          nowMoment.hour(),
          nowMoment.minute(),
        );
      }
      if (
        this.data.dateValue != null &&
        this.data.timeValue != null &&
        this.data.timezoneValue != null
      ) {
        let setMoment: CubiqTS.Moment = this._oeTimeMachine.createMoment4Strings(
          this.data.dateValue,
          this.data.timeValue,
          this.data.timezoneValue,
        );
        if (
          setMoment != null &&
          setMoment.isValid() &&
          setMoment.diff(nowMoment, "minutes") <= 59
        ) {
          this.data.dateValue = nowMoment.format("DD-MMM-YYYY");
        }
        /// assign manually entered date
        if (setMoment != null && setMoment.isValid()) {
          let metaDate = null;
          let sYear = parseInt(setMoment.format("YYYY"), 10);
          let sMonth = parseInt(setMoment.format("M"), 10) - 1;
          let sDay = parseInt(setMoment.format("D"), 10);
          this.createMonth(sMonth, sYear);
          this.dates.forEach((week) => {
            metaDate = week.find((item) => {
              return item.year === sYear && item.month === sMonth && item.day === sDay;
            });
            if (metaDate != null) {
              this.selectDate(metaDate);
            }
          });
        }
      }
    }
  }

  resetCalendarToShowDate(dateValue: string): void {
    let selectedDate: Date;
    if (!dateValue) {
      selectedDate = moment().toDate();
    } else {
      selectedDate = moment(dateValue, "DD-MMM-YYYY").toDate();
    }
    let selMonth: number = selectedDate.getMonth();
    let selYear: number = selectedDate.getFullYear();
    if (this.currentMonth != selMonth || this.currentYear != selYear) {
      this.currentMonth = selMonth;
      this.currentYear = selYear;
      this.createMonth(selMonth, selYear);
    }
    this.createMonth(this.currentMonth, this.currentYear);
  }

  onInputKeydown(event) {
    if (event.keyCode === 27 || event.keyCode === 13) {
      if (this.parseInput == false) {
        event.preventDefault();
      }
      this.hideOverlay();
    } else if (event.keyCode === 9) {
      this.hideOverlay();
    }
  }

  onMonthDropdownChange(m: string) {
    this.currentMonth = parseInt(m);
    this.onMonthChange.emit({
      month: this.currentMonth + 1,
      year: this.currentYear,
    });
    this.createMonth(this.currentMonth, this.currentYear);
  }

  onYearDropdownChange(y: string) {
    this.currentYear = parseInt(y);
    this.onYearChange.emit({
      month: this.currentMonth + 1,
      year: this.currentYear,
    });
    this.createMonth(this.currentMonth, this.currentYear);
  }

  incrementHour(event) {
    const prevHour = this.currentHour;
    const newHour = this.currentHour + this.stepHour;

    if (this.validateHour(newHour)) {
      if (this.hourFormat == "24") this.currentHour = newHour >= 24 ? newHour - 24 : newHour;
      else if (this.hourFormat == "12") {
        // Before the AM/PM break, now after
        if (prevHour < 12 && newHour > 11) {
          this.pm = !this.pm;
        }

        this.currentHour = newHour >= 13 ? newHour - 12 : newHour;
      }

      this.updateTime();
    }

    event.preventDefault();
  }

  decrementHour(event) {
    const newHour = this.currentHour - this.stepHour;

    if (this.validateHour(newHour)) {
      if (this.hourFormat == "24") this.currentHour = newHour < 0 ? 24 + newHour : newHour;
      else if (this.hourFormat == "12") {
        // If we were at noon/midnight, then switch
        if (this.currentHour === 12) {
          this.pm = !this.pm;
        }
        this.currentHour = newHour <= 0 ? 12 + newHour : newHour;
      }

      this.updateTime();
    }

    event.preventDefault();
  }

  validateHour(hour): boolean {
    let valid: boolean = true;
    let value: any = this.value;
    if (this.isRangeSelection()) {
      value = this.value[1] || this.value[0];
    }
    if (this.isMultipleSelection() && Array.isArray(this.value)) {
      value = this.value[this.value.length - 1];
    }
    let valueDateString = value ? value.toDateString() : null;

    if (this.minDate && valueDateString && this.minDate.toDateString() === valueDateString) {
      if (this.minDate.getHours() > hour) {
        valid = false;
      }
    }

    if (this.maxDate && valueDateString && this.maxDate.toDateString() === valueDateString) {
      if (this.maxDate.getHours() < hour) {
        valid = false;
      }
    }

    return valid;
  }

  incrementMinute(event) {
    let newMinute = this.currentMinute + this.stepMinute;
    if (this.validateMinute(newMinute)) {
      this.currentMinute = newMinute > 59 ? newMinute - 60 : newMinute;
      this.updateTime();
    }

    event.preventDefault();
  }

  decrementMinute(event) {
    let newMinute = this.currentMinute - this.stepMinute;
    if (this.validateMinute(newMinute)) {
      this.currentMinute = newMinute < 0 ? 60 + newMinute : newMinute;
      this.updateTime();
    }

    event.preventDefault();
  }

  validateMinute(minute): boolean {
    let valid: boolean = true;
    let value = this.value;
    if (this.isRangeSelection()) {
      value = this.value[1] || this.value[0];
    }
    if (this.isMultipleSelection() && Array.isArray(this.value)) {
      value = this.value[this.value.length - 1];
    }
    let valueDateString = value && !Array.isArray(value) ? value.toDateString() : null;

    if (this.minDate && valueDateString && this.minDate.toDateString() === valueDateString) {
      if (this.minDate.getMinutes() > minute) {
        valid = false;
      }
    }

    if (this.maxDate && valueDateString && this.maxDate.toDateString() === valueDateString) {
      if (this.maxDate.getMinutes() < minute) {
        valid = false;
      }
    }

    return valid;
  }

  incrementSecond(event) {
    let newSecond = this.currentSecond + this.stepSecond;
    if (this.validateSecond(newSecond)) {
      this.currentSecond = newSecond > 59 ? newSecond - 60 : newSecond;
      this.updateTime();
    }

    event.preventDefault();
  }

  decrementSecond(event) {
    let newSecond = this.currentSecond - this.stepSecond;
    if (this.validateSecond(newSecond)) {
      this.currentSecond = newSecond < 0 ? 60 + newSecond : newSecond;
      this.updateTime();
    }

    event.preventDefault();
  }

  validateSecond(second): boolean {
    let valid: boolean = true;
    let value: any = this.value;
    if (this.isRangeSelection()) {
      value = this.value[1] || this.value[0];
    }
    if (this.isMultipleSelection() && Array.isArray(this.value)) {
      value = this.value[this.value.length - 1];
    }
    let valueDateString = value ? value.toDateString() : null;

    if (this.minDate && valueDateString && this.minDate.toDateString() === valueDateString) {
      if (this.minDate.getSeconds() > second) {
        valid = false;
      }
    }

    if (this.maxDate && valueDateString && this.maxDate.toDateString() === valueDateString) {
      if (this.maxDate.getSeconds() < second) {
        valid = false;
      }
    }

    return valid;
  }

  updateTime() {
    let value: any = this.value;
    if (this.isRangeSelection()) {
      value = this.value[1] || this.value[0];
    }
    if (this.isMultipleSelection() && Array.isArray(this.value)) {
      value = this.value[this.value.length - 1];
    }
    value = value ? new Date(value.getTime()) : new Date();

    if (this.utc) {
      if (this.hourFormat == "12") {
        if (this.currentHour === 12) value.setUTCHours(this.pm ? 12 : 0);
        else value.setUTCHours(this.pm ? this.currentHour + 12 : this.currentHour);
      } else {
        value.setUTCHours(this.currentHour);
      }
    } else {
      if (this.hourFormat == "12") {
        if (this.currentHour === 12) value.setHours(this.pm ? 12 : 0);
        else value.setHours(this.pm ? this.currentHour + 12 : this.currentHour);
      } else {
        value.setHours(this.currentHour);
      }
    }

    value.setMinutes(this.currentMinute);
    value.setSeconds(this.currentSecond);
    if (this.isRangeSelection()) {
      if (this.value[1]) {
        value = [this.value[0], value];
      } else {
        value = [value, null];
      }
    }
    if (this.isMultipleSelection() && Array.isArray(this.value)) {
      value = [...this.value.slice(0, -1), value];
    }
    this.updateModel(value);
    this.onSelect.emit(value);
    this.updateInputField();
  }

  toggleAMPM(event) {
    this.pm = !this.pm;
    this.updateTime();
    event.preventDefault();
  }

  onUserInput(event) {
    let val = event.target.value;
    try {
      let value = this.parseValueFromString(val);
      if (value != null) {
        this.updateModel(value);
        this.updateUI();
      } else {
        // ignore pending input
        return;
      }
    } catch (err) {
      //invalid date
      this.updateModel(null);
    }

    this.filled = val != null && val.length;
    this.onInput.emit(event);
  }

  parseValueFromString(text: string): Date {
    if (!text || text.trim().length === 0) {
      return null;
    }

    let value: any;

    if (this.isSingleSelection()) {
      value = this.parseDateTime(text);
    } else if (this.isMultipleSelection()) {
      let tokens = text.split(",");
      value = [];
      for (let token of tokens) {
        value.push(this.parseDateTime(token.trim()));
      }
    } else if (this.isRangeSelection()) {
      let tokens = text.split(" - ");
      value = [];
      for (let i = 0; i < tokens.length; i++) {
        value[i] = this.parseDateTime(tokens[i].trim());
      }
    }

    return value;
  }

  parseDateTime(text): Date {
    let date: Date;
    let parts: string[] = text.split(" ");

    if (this.timeOnly) {
      date = new Date();
      this.populateTime(date, parts[0], parts[1]);
    } else {
      if (this.showTime) {
        date = this.parseDate(parts[0], this.dateFormat);
        this.populateTime(date, parts[1], parts[2]);
      } else {
        date = this.parseDate(text, this.dateFormat);
      }
    }

    return date;
  }

  populateTime(value, timeString, ampm) {
    if (this.hourFormat == "12" && !ampm) {
      throw "Invalid Time";
    }

    this.pm = ampm === "PM" || ampm === "pm";
    let time = this.parseTime(timeString);
    if (!this.utc) value.setHours(time.hour);
    else value.setUTCHours(time.hour);

    value.setMinutes(time.minute);
    value.setSeconds(time.second);
  }

  updateUI() {
    let val = this.value || this.defaultDate || new Date();

    if (Array.isArray(val)) {
      val = val[0];
    }

    this.createMonth(val.getMonth(), val.getFullYear());

    if (this.showTime || this.timeOnly) {
      let hours = this.utc ? val.getUTCHours() : val.getHours();

      if (this.hourFormat == "12") {
        this.pm = hours > 11;

        if (hours >= 12) {
          this.currentHour = hours == 12 ? 12 : hours - 12;
        } else {
          this.currentHour = hours == 0 ? 12 : hours;
        }
      } else {
        this.currentHour = this.utc ? val.getUTCHours() : val.getHours();
      }

      this.currentMinute = val.getMinutes();
      this.currentSecond = val.getSeconds();
    }
  }

  onDatePickerClick(event) {
    this.datepickerClick = true;
  }

  showOverlay() {
    if (!this.overlayVisible) {
      this.onShowOverlay.emit();
    }

    this.overlayVisible = true;

    this.overlayShown = true;

    if (this.autoZIndex) {
      this.overlayViewChild.nativeElement.style.zIndex = String(
        this.baseZIndex + ++DomHandler.zindex,
      );
    }

    this.bindDocumentClickListener();

    if (this.parseInput) {
      return;
    }

    setTimeout(() => {
      this.overlayViewChild?.nativeElement?.focus();
    }, 0);
  }

  alignOverlay() {
    if (this.domHandlerService.isBrowser()) {
      if (this.appendTo) {
        if (this.alignToAutocompleteInput && this.autoCompletePosition?.el?.nativeElement != null) {
          DomHandler.absolutePosition(
            this.overlayViewChild.nativeElement,
            this.autoCompletePosition.el.nativeElement,
          );
        } else {
          DomHandler.absolutePosition(
            this.overlayViewChild.nativeElement,
            this.inputfieldViewChild.nativeElement,
          );
        }
      } else
        DomHandler.relativePosition(
          this.overlayViewChild.nativeElement,
          this.inputfieldViewChild.nativeElement,
        );
    }
  }

  writeValue(value: any): void {
    if (moment.isMoment(value)) {
      value = value.toDate();
    }
    this.value = value;
    if (this.value && typeof this.value === "string") {
      this.value = this.parseValueFromString(this.value);
    }
    if (this.value) {
      this.data.dateValue = this.formatDateTime(this.value);
      this.inputted = true;
    } else {
      this.data.dateValue = null;
      this.inputted = false;
    }
    this.updateInputField();
    this.updateUI();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  formatTime(date) {
    if (!date) {
      return "";
    }

    let output = "";
    let hours = this.utc ? date.getUTCHours() : date.getHours();
    let minutes = date.getMinutes();
    let seconds = date.getSeconds();

    if (this.hourFormat == "12" && hours > 11 && hours != 12) {
      hours -= 12;
    }

    output += hours < 10 ? "0" + hours : hours;
    output += ":";
    output += minutes < 10 ? "0" + minutes : minutes;

    if (this.showSeconds) {
      output += ":";
      output += seconds < 10 ? "0" + seconds : seconds;
    }

    if (this.hourFormat == "12") {
      output += date.getHours() > 11 ? " PM" : " AM";
    }

    return output;
  }

  parseTime(value) {
    let tokens: string[] = value.split(":");
    let validTokenLength = this.showSeconds ? 3 : 2;

    if (tokens.length !== validTokenLength) {
      throw "Invalid time";
    }

    let h = parseInt(tokens[0]);
    let m = parseInt(tokens[1]);
    let s = this.showSeconds ? parseInt(tokens[2]) : null;

    if (
      isNaN(h) ||
      isNaN(m) ||
      h > 23 ||
      m > 59 ||
      (this.hourFormat == "12" && h > 12) ||
      (this.showSeconds && (isNaN(s) || s > 59))
    ) {
      throw "Invalid time";
    } else {
      if (this.hourFormat == "12" && h !== 12 && this.pm) {
        h += 12;
      }

      return { hour: h, minute: m, second: s };
    }
  }

  // Ported from jquery-ui datepicker parseDate
  parseDate(value, format) {
    try {
      let parsedDate = this.parseDateActual(value, format);
      return parsedDate;
    } catch (error) {
      for (let i = 0; i < this.altDateFormats.length; i++) {
        try {
          let parsedDate = this.parseDateActual(value, this.altDateFormats[i]);
          return parsedDate;
        } catch (error) {}
      }
    }
  }
  parseDateActual(value, format) {
    if (format == null || value == null) {
      throw "Invalid arguments";
    }

    value = typeof value === "object" ? value.toString() : value + "";
    if (value === "") {
      return null;
    }

    let iFormat,
      dim,
      extra,
      iValue = 0,
      shortYearCutoff =
        typeof this.shortYearCutoff !== "string"
          ? this.shortYearCutoff
          : (new Date().getFullYear() % 100) + parseInt(this.shortYearCutoff, 10),
      year = -1,
      month = -1,
      day = -1,
      doy = -1,
      literal = false,
      date,
      lookAhead = (match) => {
        let matches = iFormat + 1 < format.length && format.charAt(iFormat + 1) === match;
        if (matches) {
          iFormat++;
        }
        return matches;
      },
      getNumber = (match) => {
        let isDoubled = lookAhead(match),
          size =
            match === "@"
              ? 14
              : match === "!"
                ? 20
                : match === "y" && isDoubled
                  ? 4
                  : match === "o"
                    ? 3
                    : 2,
          minSize = match === "y" ? size : 1,
          digits = new RegExp("^\\d{" + minSize + "," + size + "}"),
          num = value.substring(iValue).match(digits);
        if (!num) {
          throw "Missing number at position " + iValue;
        }
        iValue += num[0].length;
        return parseInt(num[0], 10);
      },
      getName = (match, shortNames, longNames) => {
        let index = -1;
        let arr = lookAhead(match) ? longNames : shortNames;
        let names = [];

        for (let i = 0; i < arr.length; i++) {
          names.push([i, arr[i]]);
        }
        names.sort((a, b) => {
          return -(a[1].length - b[1].length);
        });

        for (let i = 0; i < names.length; i++) {
          let name = names[i][1];
          if (value.substr(iValue, name.length).toLowerCase() === name.toLowerCase()) {
            index = names[i][0];
            iValue += name.length;
            break;
          }
        }

        if (index !== -1) {
          return index + 1;
        } else {
          throw "Unknown name at position " + iValue;
        }
      },
      checkLiteral = () => {
        if (value.charAt(iValue) !== format.charAt(iFormat)) {
          throw "Unexpected literal at position " + iValue;
        }
        iValue++;
      };

    for (iFormat = 0; iFormat < format.length; iFormat++) {
      if (literal) {
        if (format.charAt(iFormat) === "'" && !lookAhead("'")) {
          literal = false;
        } else {
          checkLiteral();
        }
      } else {
        switch (format.charAt(iFormat)) {
          case "d":
            day = getNumber("d");
            break;
          case "D":
            getName("D", this.locale.dayNamesShort, this.locale.dayNames);
            break;
          case "o":
            doy = getNumber("o");
            break;
          case "m":
            month = getNumber("m");
            break;
          case "M":
            month = getName("M", this.locale.monthNamesShort, this.locale.monthNames);
            break;
          case "y":
            year = getNumber("y");
            break;
          case "@":
            date = new Date(getNumber("@"));
            year = date.getFullYear();
            month = date.getMonth() + 1;
            day = date.getDate();
            break;
          case "!":
            date = new Date((getNumber("!") - this.ticksTo1970) / 10000);
            year = date.getFullYear();
            month = date.getMonth() + 1;
            day = date.getDate();
            break;
          case "'":
            if (lookAhead("'")) {
              checkLiteral();
            } else {
              literal = true;
            }
            break;
          default:
            checkLiteral();
        }
      }
    }

    if (iValue < value.length) {
      extra = value.substr(iValue);
      if (!/^\s+/.test(extra)) {
        throw "Extra/unparsed characters found in date: " + extra;
      }
    }

    if (year === -1) {
      year = new Date().getFullYear();
    } else if (year < 100) {
      year +=
        new Date().getFullYear() -
        (new Date().getFullYear() % 100) +
        (year <= shortYearCutoff ? 0 : -100);
    }

    if (doy > -1) {
      month = 1;
      day = doy;
      do {
        dim = this.getDaysCountInMonth(year, month - 1);
        if (day <= dim) {
          break;
        }
        month++;
        day -= dim;
      } while (true);
    }

    if (this.utc) {
      date = new Date(Date.UTC(year, month - 1, day));
      if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() + 1 !== month ||
        date.getUTCDate() !== day
      ) {
        throw "Invalid date"; // E.g. 31/02/00
      }
    } else {
      date = this.daylightSavingAdjust(new Date(year, month - 1, day));
      if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
        throw "Invalid date"; // E.g. 31/02/00
      }
    }

    if (date != null && this.minDate != null && date < this.minDate) {
      return null;
    }

    return date;
  }

  daylightSavingAdjust(date) {
    if (!date) {
      return null;
    }

    if (!this.utc) {
      date.setHours(date.getHours() > 12 ? date.getHours() + 2 : 0);
    }

    return date;
  }

  updateFilledState() {
    this.filled = this.inputFieldValue && this.inputFieldValue != "" && this.inputFieldValue != " ";
  }

  onTodayButtonClick(event) {
    let date: Date = new Date();
    let dateMeta = {
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      today: true,
      selectable: true,
    };

    this.createMonth(dateMeta.month, dateMeta.year);
    const selectIsSave = this.selectIsSave;
    this.selectIsSave = false;
    this.onDateSelect(null, dateMeta);
    this.selectIsSave = selectIsSave;
    event.preventDefault();
    this.onTodayClick.emit(event);
  }

  onClearButtonClick(event) {
    this.updateModel(null);
    this.updateInputField();
    this.hideOverlay();
    this.onClearClick.emit(event);
  }

  bindDocumentClickListener() {
    if (this.documentClickListener == null) {
      this.documentClickListener = this.renderer.listen("document", "click", (event) => {
        const path = getPath(event.target);

        if (
          this.overlayVisible &&
          !this.datepickerClick &&
          path.every(
            (target) => !target.classList.contains("interface-control__time-input__dropdown"),
          )
        ) {
          this.onCancelClick();
          this.onClose.emit(event);
          this.unbindDocumentClickListener();
        }
        this.datepickerClick = false;
        this.cd.detectChanges();
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener != null) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  ngOnDestroy() {
    this.unbindDocumentClickListener();

    if (this.appendTo) {
      this.el.nativeElement.appendChild(this.overlayViewChild.nativeElement);
    }
  }

  onTimeInputFocus(event) {
    // if (
    //   this.inputTimeField.data.timeValue != null ||
    //   this.inputTimeField.state === ICNInputControlState.VALID ||
    //   this.inputTimeField.state === ICNInputControlState.INVALID
    // )
    //   this.inputTimeField.state = ICNInputControlState.ACTIVE;
  }

  onTimeInputBlur(event) {
    // this.state = event.internalState;
    // if (this.inputTimeField.data.timeValue != null)
    //   this.inputTimeField.state = ICNInputControlState.INACTIVE;
  }

  onSaveClick(): void {
    if (this.value) {
      this.data.dateValue = this.formatDateTime(this.value);
      this.data.marketValue = "CUSTOM";
      this.dateValueInput.emit(this.data);
      if (
        this.inputTimeField.state === ICNInputControlState.INVALID ||
        this.inputTimeField.state === ICNInputControlState.INACTIVE
      ) {
        this.inputTimeField.externalState = ICNInputControlState.VALID;
      } else {
        this.inputTimeField.externalState = null;
      }
      this.updateInputField();
    } else {
      this.state = ICNInputControlState.INVALID;
    }
  }

  onCancelClick() {
    this.data.dateValue = this.prevData.dateValue;
    this.data.timeValue = this.prevData.timeValue;
    this.data.timezoneValue = this.prevData.timezoneValue;
    if (this.inputTimeField) {
      this.inputTimeField.data.timeValue = this.data.timeValue;
      if (this.data.timezoneValue) {
        this.inputTimeField.data.timezoneValue = new ICAutocompleteGenericModel(
          this.data.timezoneValue,
          this.data.timezoneValue,
        );
      } else if (this.inputTimeField.data.userTimezoneCode) {
        this.inputTimeField.data.timezoneValue = new ICAutocompleteGenericModel(
          this.inputTimeField.data.userTimezoneCode,
          this.inputTimeField.data.userTimezoneCode,
        );
      }
    }
    if (this.data.dateValue) {
      this.updateModel(moment(this.data.dateValue, "DD-MMM-YYYY").toDate());
    }
    this.updateInputField();
    this.hideOverlay();
    this.state = null;
  }

  public setOverlayVisibility(visibility: boolean, target: string): void {
    logger.out.info(
      `DESKTOP: overlayVisibility: current state: ${this.overlayVisible} | new state: ${visibility} | remote target: ${target} | local target: ${this.target}`,
    );
    this.overlayVisible = visibility;
  }

  onFieldIconClick($event, type, index) {
    if (type === "end" && index === 0) {
      this.onButtonClick($event);
    }
    this.fieldIconClick.next({ event: $event, type: type, index: index });
  }

  onFieldPointerOver(_) {
    if (this.fieldState == "enabled") {
      this.fieldPointerOver = true;
      this.fieldState = "hover";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    }
  }

  onFieldPointerOut(_) {
    if (this.fieldState != "active") {
      this.fieldPointerOver = false;
      this.fieldState = this.disabled
        ? "disabled"
        : this.state === ICNInputControlState.INVALID
          ? "invalid"
          : "enabled";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    } else {
      // state changes deferred until blur completes.
      this.fieldPointerOver = false;
    }
  }
}
